/* Grid Export Service */
/* @author Gopalakrishnan */
/* @Filename grid-export.service.ts */

/*
    Handles the export xhr call initaited from grid controller
*/

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';

import { LogService, ServiceRouter } from '@pershing/pershing-core';
import { GridExportServiceDefinition } from '../service-definitions/grid-export-service-definition';

import { timeout } from 'rxjs/operators';
import { GridOptions } from 'ag-grid';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ExportErrorDialog } from '../components/pershing-grid-controller.component';

const SERVICE_CALL_TIMEOUT = 300000;
const COMMON_DOWNLOAD_MESSAGE = 'Requested report is being generated and it will be available shortly in the downloads folder.';

const COMMON_DOWNLOAD_ACTION_MESSAGE = '';

@Injectable()
export class GridExportService {

    public exportServiceUrl: string;
    public settingsDetailsServiceUrl: string;

    constructor(private http: HttpClient, private logService: LogService, private serviceRouter: ServiceRouter, private _snackBar: MatSnackBar, public dialog: MatDialog) {
        logService.info('GridExportService : constructor Initializing');
        this.exportServiceUrl = this.serviceRouter.getServiceRouteURL(GridExportServiceDefinition.getInstance(), { ignoreOptions: { serviceVersion: true } });
    }
    private openDownloadStartMessage() {
        this._snackBar.open(COMMON_DOWNLOAD_MESSAGE, COMMON_DOWNLOAD_ACTION_MESSAGE, {
            duration: 3000
        });
    }

    openErrorDialog(): void {
        this._snackBar.dismiss();
        this.dialog.open(ExportErrorDialog, {
            width: '350px',
            panelClass: 'modal',
            data: null
        });
    }

    public initiateExportCallWithServiceEndpoint(dataServiceURL: string, exportConfig: any, downloadExtension: string, fileName?: string): void {
        let headerObj = new HttpHeaders();
        headerObj.append('Content-Type', 'application/x-www-form-urlencoded');
        let params = new HttpParams();
        params = params.append('url', dataServiceURL);
        params = params.append('exportConfig', JSON.stringify(exportConfig));

        const url = this.exportServiceUrl + '/exportData';

        this.openDownloadStartMessage();
        this.http.post(url + '.' + downloadExtension, params, { headers: headerObj, responseType: 'blob', observe: 'response' })
            .pipe(timeout(SERVICE_CALL_TIMEOUT)).subscribe((resp: HttpResponse<Blob>) => {
                this.handleExportResponse(resp, exportConfig, downloadExtension, fileName);
            },
                error => {
                    this.openErrorDialog();
                    this.logService.error('ERROR: GridExportService - initiateExportCall : ', error);
                });
    }

    public initiateExportCallWithCSVData(gridOptions: GridOptions, exportConfig: any, downloadExtension: string, fileName?: string): void {
        let headerObj = new HttpHeaders();
        headerObj.append('Content-Type', 'application/x-www-form-urlencoded');
        let params = new HttpParams();
        params = params.append('gridCSVData', this.getCSVData(gridOptions, exportConfig));
        params = params.append('exportConfig', JSON.stringify(exportConfig));

        let url = '';
        if (downloadExtension === 'pdf') {
            url = this.exportServiceUrl + '/griddatatopdf';
        } else if (downloadExtension === 'xlsx') {
            url = this.exportServiceUrl + '/griddatatoexcel';
        }

        this.openDownloadStartMessage();
        this.http.post(url, params, { headers: headerObj, responseType: 'blob', observe: 'response' })
            .pipe(timeout(SERVICE_CALL_TIMEOUT)).subscribe((resp: HttpResponse<Blob>) => {
                this.handleExportResponse(resp, exportConfig, downloadExtension, fileName);
            },
                error => {
                    this.openErrorDialog();
                    this.logService.error('ERROR: GridExportService - initiateExportCall : ', error);
                });
    }

    private getCSVData(gridOptions: GridOptions, exportConfig: any): string {
        let csvExport = '';
        let params: any = {
            exportMode: '.csv'
        };
        params.columnKeys = exportConfig.columnInfoList.map(cl => cl.column);

        params.processCellCallback = param => {
            let returnValue = param.column.colDef.valueFormatter ? param.column.colDef.valueFormatter(param) : param.value;
            return returnValue;
        };

        if (gridOptions && gridOptions.api) {
            csvExport = gridOptions.api.getDataAsCsv(params);
        }

        csvExport = this.encodeGridData(csvExport, exportConfig);

        return csvExport;
    }

    public initiateExportCallWithJSONData(jsonString: string, exportConfig: any, downloadExtension: string, fileName?: string): void {
        let headerObj = new HttpHeaders();
        headerObj.append('Content-Type', 'application/x-www-form-urlencoded');
        let params = new HttpParams();
        params = params.append('gridJSONData', this.encodeGridData(jsonString, exportConfig));
        params = params.append('exportConfig', JSON.stringify(exportConfig));

        let url = '';
        if (downloadExtension === 'pdf') {
            url = this.exportServiceUrl + '/gridjsondatatopdf';
        } else if (downloadExtension === 'xlsx') {
            url = this.exportServiceUrl + '/gridjsondatatoexcel';
        }

        this.openDownloadStartMessage();
        this.http.post(url, params, { headers: headerObj, responseType: 'blob', observe: 'response' })
            .pipe(timeout(SERVICE_CALL_TIMEOUT)).subscribe((resp: HttpResponse<Blob>) => {
                this.handleExportResponse(resp, exportConfig, downloadExtension, fileName);
            },
                error => {
                    this.openErrorDialog();
                    this.logService.error('ERROR: GridExportService - initiateExportCall : ', error);
                });
    }

    private encodeGridData(gridData: any, exportConfig: any): any {
        let returnData = gridData;
        if (exportConfig && exportConfig['dataEncodedWithBase64'] && typeof exportConfig['dataEncodedWithBase64'] === 'string') {
            let base64Encode = exportConfig['dataEncodedWithBase64'].toUpperCase();
            if (base64Encode === 'YES' || base64Encode === 'TRUE') {
                returnData = btoa(gridData);
            }
        } else {
            returnData = btoa(gridData);
        }
        return returnData;
    }

    private handleExportResponse(resp: HttpResponse<Blob>, exportConfig: any, downloadExtension: string, fileName?: string) {
        this._snackBar.dismiss();
        this.logService.log('Success: GridExportService - initiateExportCall');
        if (window.navigator && window.navigator.msSaveOrOpenBlob) { // for IE
            window.navigator.msSaveOrOpenBlob(resp.body, this.getFileName(resp.headers, fileName, downloadExtension, exportConfig));
        } else {
            let downloadElement = document.createElement('a');
            downloadElement.setAttribute('href', window.URL.createObjectURL(resp.body));
            downloadElement.setAttribute('download', this.getFileName(resp.headers, fileName, downloadExtension, exportConfig) );
            downloadElement.click();
        }
    }

    private getFileName( headers: HttpHeaders, fileName: string, downloadExtension: string , exportConfig: any): string {
        let returnfileName = null;
        if (headers && headers.get('Content-Disposition') !== null &&  headers.get('Content-Disposition') !== undefined) {
            headers.get('Content-Disposition').split(';').forEach((i) => {
                if (i.includes('filename') && i.split('=').length === 2) {
                    returnfileName = i.split('=')[1].replace(/"/g, '');
                }
            });
        }
        if (returnfileName === null) {
            if (fileName === undefined || fileName === null || fileName === '') {
                if (exportConfig && exportConfig['title']) {
                    returnfileName = exportConfig['title'];
                    if (exportConfig['contextValue']) {
                        returnfileName = returnfileName + '_' + exportConfig['contextValue'];
                    }
                } else {
                    returnfileName = 'exportfile';
                }
            } else {
                returnfileName = fileName;
            }
            returnfileName = returnfileName.replace(/ /g, '+');
            returnfileName += ('.' + downloadExtension);
        }
        return returnfileName;
    }

}
